/**
 * theme.js
 *
 * THEMES below is Hirality's actual token structure/palette (dark: navy +
 * cyan/purple/green/amber/red; light: cream + matching accents) -- see
 * ThemeContext.jsx for the CSS-variable injection + useTheme()/ThemeToggle
 * that consumes it, mirroring Hirality HRMS's real ThemeContext.jsx.
 *
 * buildMuiTheme(T) is the one addition beyond Hirality's pattern: this ATS
 * frontend is built with MUI components (Button, TextField, Paper, etc.),
 * which read colors from an MUI theme object rather than raw CSS
 * variables. This function translates the same T tokens into an MUI theme
 * so every existing component re-colors automatically when the mode
 * toggles -- nothing had to be rewritten component-by-component.
 */
import { createTheme } from "@mui/material/styles";

export const THEMES = {
  dark: {
    bg:        "#0B1220",
    card:      "#111B2E",
    card2:     "#0E1626",
    border:    "rgba(59,130,246,0.09)",
    cyan:      "#3B82F6",
    purple:    "#6366F1",
    green:     "#10B981",
    amber:     "#F59E0B",
    red:       "#EF4444",
    text:      "#E5EBF5",
    muted:     "#7C8CA6",
    dim:       "#3D4F6B",
    inp_bg:    "rgba(255,255,255,0.04)",
    opt_bg:    "#111B2E",
    toggle_bg: "rgba(255,255,255,0.08)",
    scheme:    "dark",
    sidebar_bg:      "#0E1626",
    sidebar_border:  "rgba(59,130,246,0.09)",
    sidebar_hover:   "rgba(59,130,246,0.07)",
    sidebar_active:  "rgba(59,130,246,0.12)",
    header_bg:       "#111B2E",
    header_border:   "rgba(59,130,246,0.09)",
    nav_text:        "#7C8CA6",
    nav_text_active: "#3B82F6",
    submenu_bg:      "rgba(59,130,246,0.04)",
    dropdown_bg:     "#111B2E",
    shadow:          "0 8px 32px rgba(0,0,0,0.4)",
    blue:            "#60A5FA",
    border_strong:   "rgba(59,130,246,0.2)",
    card_hover:      "#16223A",
    cyan_soft:       "rgba(59,130,246,0.12)",
    purple_soft:     "rgba(99,102,241,0.12)",
    green_soft:      "rgba(16,185,129,0.12)",
    amber_soft:      "rgba(245,158,11,0.12)",
    red_soft:        "rgba(239,68,68,0.12)",
    blue_soft:       "rgba(96,165,250,0.12)",
    green_soft2:     "rgba(16,185,129,0.15)",
    red_soft2:       "rgba(239,68,68,0.15)",
    amber_soft2:     "rgba(245,158,11,0.15)",
    amber_border:    "rgba(245,158,11,0.3)",
    cyan_border:     "rgba(59,130,246,0.3)",
    purple_border:   "rgba(99,102,241,0.3)",
    green_border:    "rgba(16,185,129,0.3)",
    red_border:      "rgba(239,68,68,0.3)",
    cyan_faint:      "rgba(59,130,246,0.06)",
    surface_soft:    "rgba(255,255,255,0.05)",
    surface_med:     "rgba(255,255,255,0.07)",
    surface_faint:   "rgba(255,255,255,0.02)",
    hover_border:    "rgba(255,255,255,0.1)",
    shadow_soft:     "0 8px 32px rgba(0,0,0,0.2)",
  },
  light: {
    bg:        "#F5F7FA",
    card:      "#FFFFFF",
    card2:     "#EEF2F7",
    border:    "rgba(15,23,42,0.08)",
    cyan:      "#2563EB",
    purple:    "#4F46E5",
    green:     "#059669",
    amber:     "#D97706",
    red:       "#DC2626",
    text:      "#111827",
    muted:     "#5A6B82",
    dim:       "#9BA8B8",
    inp_bg:    "#F1F5F9",
    opt_bg:    "#FFFFFF",
    toggle_bg: "rgba(0,0,0,0.06)",
    scheme:    "light",
    sidebar_bg:      "#F5F7FA",
    sidebar_border:  "rgba(15,23,42,0.08)",
    sidebar_hover:   "#EEF2F7",
    sidebar_active:  "#E4ECFC",
    header_bg:       "#F5F7FA",
    header_border:   "rgba(15,23,42,0.08)",
    nav_text:        "#5A6B82",
    nav_text_active: "#2563EB",
    submenu_bg:      "#EEF2F7",
    dropdown_bg:     "#FFFFFF",
    shadow:          "0 8px 32px rgba(0,0,0,0.12)",
    blue:            "#1D4ED8",
    border_strong:   "rgba(37,99,235,0.2)",
    card_hover:      "#EEF2F7",
    cyan_soft:       "rgba(37,99,235,0.12)",
    purple_soft:     "rgba(79,70,229,0.12)",
    green_soft:      "rgba(5,150,105,0.12)",
    amber_soft:      "rgba(217,119,6,0.12)",
    red_soft:        "rgba(220,38,38,0.12)",
    blue_soft:       "rgba(29,78,216,0.12)",
    green_soft2:     "rgba(5,150,105,0.15)",
    red_soft2:       "rgba(220,38,38,0.15)",
    amber_soft2:     "rgba(217,119,6,0.15)",
    amber_border:    "rgba(217,119,6,0.3)",
    cyan_border:     "rgba(37,99,235,0.3)",
    purple_border:   "rgba(79,70,229,0.3)",
    green_border:    "rgba(5,150,105,0.3)",
    red_border:      "rgba(220,38,38,0.3)",
    cyan_faint:      "rgba(37,99,235,0.06)",
    surface_soft:    "rgba(0,0,0,0.04)",
    surface_med:     "rgba(0,0,0,0.05)",
    surface_faint:   "rgba(0,0,0,0.02)",
    hover_border:    "rgba(0,0,0,0.1)",
    shadow_soft:     "0 8px 32px rgba(0,0,0,0.08)",
  },
};

export function buildMuiTheme(T) {
  return createTheme({
    palette: {
      mode: T.scheme,
      primary: { main: T.cyan },
      secondary: { main: T.purple },
      background: { default: T.bg, paper: T.card },
      text: { primary: T.text, secondary: T.muted },
      divider: T.border,
      error: { main: T.red },
      warning: { main: T.amber },
      success: { main: T.green },
      info: { main: T.blue },
    },
    typography: {
      fontFamily: '"Outfit", "Helvetica", "Arial", sans-serif',
      h1: { fontWeight: 700 },
      h2: { fontWeight: 700 },
      h3: { fontWeight: 600 },
      h4: { fontWeight: 600 },
      h5: { fontWeight: 600 },
      h6: { fontWeight: 600 },
      button: { fontWeight: 600, textTransform: "none" },
    },
    shape: { borderRadius: 12 },
    shadows: [
      "none",
      "0px 1px 3px rgba(0,0,0,0.06)",
      "0px 2px 6px rgba(0,0,0,0.07)",
      "0px 3px 8px rgba(0,0,0,0.08)",
      ...Array(21).fill(T.shadow_soft),
    ],
    components: {
      MuiCssBaseline: {
        styleOverrides: { body: { backgroundColor: T.bg, color: T.text } },
      },
      MuiPaper: {
        styleOverrides: { root: { borderRadius: 12, backgroundImage: "none", backgroundColor: T.card } },
      },
      MuiCard: {
        styleOverrides: {
          root: { borderRadius: 12, border: `1px solid ${T.border}`, backgroundColor: T.card, boxShadow: T.shadow_soft },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: { borderRadius: 10, paddingLeft: 18, paddingRight: 18 },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: { backgroundColor: T.header_bg, color: T.text, boxShadow: "none", borderBottom: `1px solid ${T.header_border}` },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: { backgroundColor: T.sidebar_bg, color: T.nav_text, borderRight: `1px solid ${T.sidebar_border}` },
        },
      },
      MuiChip: {
        styleOverrides: { root: { borderRadius: 8, fontWeight: 600 } },
      },
      MuiTableCell: {
        styleOverrides: {
          head: { fontWeight: 700, backgroundColor: T.card2, color: T.text, borderBottomColor: T.border },
          root: { borderBottomColor: T.border },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: { backgroundColor: T.inp_bg },
          notchedOutline: { borderColor: T.border },
        },
      },
      MuiTableContainer: {
        styleOverrides: { root: { backgroundColor: T.card } },
      },
    },
  });
}